import api, { APIResponse } from '../../../lib/commons/api'
import { ProjectBasic, ProjectDetail } from '../../../lib/functions/project'
import { RoleBasic } from '../../../lib/functions/role'
import { UserBasic, UserDetail } from '../../../lib/functions/user'
import { DateTerm } from '../../../utils/date'
import DateVO from '../../../vo/DateVO'
import { TreeRow } from '../../containers/BulkSheetView/model'
import { Dispatch } from 'redux'
import {
  Cockpit,
  Function as AppFunction,
} from '../../../lib/commons/appFunction'
import { RouteComponentProps } from 'react-router'
import { DivisionProps } from '../../../lib/functions/division'
import { PositionProps } from '../../../lib/functions/position'
import { AggregateTarget } from '../../../domain/entity/WbsItemEntity'
import { AlertThreshold } from './gridOptions/alertLogic'

export enum ColumnQuickFilterKey {
  INITIAL = 'INITIAL',
  RESTORE = 'RESTORE',
}

export enum TimeGrain {
  MONTH = 'MONTH',
  WEEK = 'WEEK',
}

export enum AssignmentType {
  RESPONSIBLE = 'RESPONSIBLE',
  ASSIGNEE = 'ASSIGNEE',
}

export enum MemberWorkloadStatusType {
  MEMBER = 'MEMBER',
  PROJECT = 'PROJECT',
  SUMMARY = 'SUMMARY',
  TOTAL_ESTIMATED_ACTUAL = 'TOTAL_ESTIMATED_ACTUAL',
  ESTIMATED_ACTUAL = 'ESTIMATED_ACTUAL',
}
export enum EstimatedActualType {
  DEFAULT = 'DEFAULT',
  RESOURCE_PLAN = 'RESOURCE_PLAN',
  ESTIMATED = 'ESTIMATED',
  ACTUAL = 'ACTUAL',
}

/**
 * RowData definition
 */
export interface MemberWorkMonthBody {
  yearMonth?: string
  manMonth?: number
}

export interface MemberWorkloadStatusRowBodyBase {
  resourcePlanUuid?: string
  revision?: string
  lockVersion?: number
  memberWorkMonths?: MemberWorkMonthBody[]
}

export interface MemberWorkloadStatusGroupRowsByMemberColRowBody
  extends MemberWorkloadStatusRowBodyBase {
  user?: UserBasic
  division?: DivisionProps
  position?: PositionProps
  project?: ProjectBasic
  role?: RoleBasic
  validFrom?: string
  validTo?: string
  validUser?: boolean
}

export interface MemberWorkloadStatusRow extends TreeRow {
  body: MemberWorkloadStatusGroupRowsByMemberColRowBody
  type: MemberWorkloadStatusType
  estimatedActualType: EstimatedActualType
  isRootParentRow?: boolean
  isParentRow?: boolean
}

/**
 * API response
 */
export interface MemberWorkMonthSkeleton {
  month: Date
  manMonth: number
}

export interface workloadStatusSkeleton {
  project: ProjectDetail
  resourcePlan: MemberWorkMonthSkeleton[]
  estimatedHours: MemberWorkMonthSkeleton[]
  actualHours: MemberWorkMonthSkeleton[]
}

export interface MemberWorkloadStatusSkeleton {
  uuid: string
  user: UserBasic
  validFrom: number
  validTo: number
  role: RoleBasic
  division: DivisionProps
  position: PositionProps
  workloadStatus: workloadStatusSkeleton[]
  lockVersion: number
  validUser: boolean
  revision?: string
  createdAt?: number
  createdBy?: UserBasic
  updatedAt?: number
  updatedBy?: UserBasic
}

export const fetchMemberWorkloadStatusWorkMonths = async (params: {
  startDate: string
  endDate: string
  aggregateTarget: AggregateTarget
  timeGrain: TimeGrain
  assignmentType: AssignmentType
}): Promise<MemberWorkloadStatusSkeleton[]> => {
  const response: APIResponse = await api.functional.request(
    'GET',
    '/api/v1/member_workload_status',
    params
  )
  return response.json as Promise<MemberWorkloadStatusSkeleton[]>
}

export const fetchAlertThresholds = async (): Promise<AlertThreshold> => {
  const response: APIResponse = await api.functional.request(
    'GET',
    '/api/v1/member_workload_status/thresholds'
  )
  return response.json as AlertThreshold
}

export const saveAlertThresholds = async (
  params: AlertThreshold
): Promise<void> => {
  await api.functional.request(
    'PUT',
    '/api/v1/member_workload_status/thresholds',
    params
  )
}

export const toPercentage = (threshold: AlertThreshold): AlertThreshold => ({
  resourcePlanCautionThreshold: Math.round(
    threshold.resourcePlanCautionThreshold * 100
  ),
  resourcePlanWarningThreshold: Math.round(
    threshold.resourcePlanWarningThreshold * 100
  ),
  estimatedOverCautionThreshold: Math.round(
    threshold.estimatedOverCautionThreshold * 100
  ),
  estimatedOverWarningThreshold: Math.round(
    threshold.estimatedOverWarningThreshold * 100
  ),
  estimatedUnderCautionThreshold: Math.round(
    threshold.estimatedUnderCautionThreshold * 100
  ),
  estimatedUnderWarningThreshold: Math.round(
    threshold.estimatedUnderWarningThreshold * 100
  ),
  actualOverCautionThreshold: Math.round(
    threshold.actualOverCautionThreshold * 100
  ),
  actualOverWarningThreshold: Math.round(
    threshold.actualOverWarningThreshold * 100
  ),
  actualUnderCautionThreshold: Math.round(
    threshold.actualUnderCautionThreshold * 100
  ),
  actualUnderWarningThreshold: Math.round(
    threshold.actualUnderWarningThreshold * 100
  ),
})

export const toDecimal = (threshold: AlertThreshold): AlertThreshold => ({
  resourcePlanCautionThreshold: threshold.resourcePlanCautionThreshold / 100,
  resourcePlanWarningThreshold: threshold.resourcePlanWarningThreshold / 100,
  estimatedOverCautionThreshold: threshold.estimatedOverCautionThreshold / 100,
  estimatedOverWarningThreshold: threshold.estimatedOverWarningThreshold / 100,
  estimatedUnderCautionThreshold:
    threshold.estimatedUnderCautionThreshold / 100,
  estimatedUnderWarningThreshold:
    threshold.estimatedUnderWarningThreshold / 100,
  actualOverCautionThreshold: threshold.actualOverCautionThreshold / 100,
  actualOverWarningThreshold: threshold.actualOverWarningThreshold / 100,
  actualUnderCautionThreshold: threshold.actualUnderCautionThreshold / 100,
  actualUnderWarningThreshold: threshold.actualUnderWarningThreshold / 100,
})

/**
 * functions
 */
export const createDefaultDateTerm = (): DateTerm => {
  const now = DateVO.now()
  const startYear = now.getMonth() > 3 ? now.getYear() : now.getYear() - 1
  return {
    startDate: `${startYear}/04/01`,
    endDate: `${startYear + 1}/03/31`,
  }
}

interface displayNameProps {
  displayName?: string
  name?: string
}

export const displayNameComparator = (
  valueA: string | displayNameProps,
  valueB: string | displayNameProps
): number => {
  const strA =
    typeof valueA === 'string'
      ? valueA
      : valueA?.displayName || valueA?.name || ''
  const strB =
    typeof valueB === 'string'
      ? valueB
      : valueB?.displayName || valueB?.name || ''
  return strA.localeCompare(strB)
}

/**
 * Props types
 */

export type ReduxProps = {
  dispatch: Dispatch<any>
  functions: AppFunction[]
}

export type SingleSheetKeyLite = {
  uuid: string
  projectUuid?: string
}

export type ApplicationFunctionExternalId = string

export type PageInjections = {
  externalId: string
  uuid: string
  cockpit: Cockpit
  functionLayerDepth: number
  repository?: unknown
  options?: unknown
  breadcrumbs?: (key: SingleSheetKeyLite) => any
  useUiMetaOf?: (basic: any) => ApplicationFunctionExternalId
  useFunctionUuidOf?: ApplicationFunctionExternalId
}

export type MemberWorkloadStatusProps = RouteComponentProps<{
  code?: string | undefined
}> &
  MemberWorkloadStatusBaseProps

export type MemberWorkloadStatusBaseProps = ReduxProps & PageInjections
